import { Neume, NeumeElement } from "./EBChant";

type RawChantElement = {
    text: string;
    chant: string;
    hasRedDotBefore?: boolean;
};

const isInclinatum = (char: string) => {
    return "ABCDEFGHIJKLM".indexOf(char) !== -1;
};

const getPitchFromChar = (char: string) => {
    return "abcdefghijklm".indexOf(char.toLowerCase()) - 2;
};

export const getNeumesFromGabc = (gabc: string, dropCap: boolean) => {
    const rawChantElements: RawChantElement[] = [{ text: "", chant: "" }];

    gabc = gabc.replaceAll("<sc>", "").replaceAll("</sc>", "");

    let dropCapText = "";

    let mode: "before chant" | "in chant" | "after chant" = "before chant";
    let hasRedDotBefore = false;
    for (let i = 4; i < gabc.length; i++) {
        const char = gabc[i];
        const lastElement = rawChantElements[rawChantElements.length - 1];

        if (mode === "before chant") {
            if (char === "(") {
                mode = "in chant";
            } else if (char === " ") {
                rawChantElements.push({ text: "", chant: "" });
            } else {
                if (dropCap && dropCapText.length === 0) {
                    dropCapText = char;
                } else {
                    lastElement.text += char;
                }
            }
        } else if (mode === "in chant") {
            if (char === ")") {
                mode = "after chant";
            } else if (char === "o" || char === "@") {
                // skip this char
            } else {
                lastElement.chant += char;
            }
        } else if (mode === "after chant") {
            if (char === "(") {
                mode = "in chant";
            } else if (char === " ") {
                lastElement.text += " ";
                rawChantElements.push({ text: "", chant: "" });
            } else if (char === "£") {
                hasRedDotBefore = true;
            } else {
                rawChantElements.push({
                    text: char,
                    chant: "",
                    hasRedDotBefore,
                });
                hasRedDotBefore = false;
                mode = "before chant";
            }
        }
    }

    const nonEmptyRawChantElements = rawChantElements.filter(
        (element) => element.text.length > 0 || element.chant.length > 0
    );

    const neumes: Neume[] = [];

    let flatPitch: number | undefined = undefined;
    let showFlat = false;

    nonEmptyRawChantElements.forEach((rawElement) => {
        let neumeElements: NeumeElement[] = [];

        let requirePunctum = true;

        for (let i = 0; i < rawElement.chant.length; i++) {
            const chantChar = rawElement.chant[i];

            if (chantChar === "/") {
                requirePunctum = true;
                continue;
            }

            if (chantChar === "!") {
                requirePunctum = true;
                const lastElement = neumeElements[neumeElements.length - 1];

                lastElement.suppressSpace = true;
                continue;
            }

            if (chantChar === ".") {
                continue; // Turned off
                const lastElement = neumeElements[neumeElements.length - 1];
                if (lastElement) {
                    const lastNote =
                        lastElement.notes[lastElement.notes.length - 1];
                    if (lastNote) {
                        lastNote.hasMora = true;
                    }
                }
                continue;
            }

            if (chantChar === "'") {
                const lastElement = neumeElements[neumeElements.length - 1];
                if (lastElement) {
                    const lastNote =
                        lastElement.notes[lastElement.notes.length - 1];
                    if (lastNote) {
                        lastNote.hasVerticalEpisemaBelow = true;
                    }
                }
                continue;
            }

            if (chantChar === "w") {
                const lastElement = neumeElements[neumeElements.length - 1];
                if (lastElement) {
                    const lastNote =
                        lastElement.notes[lastElement.notes.length - 1];
                    if (lastNote) {
                        lastNote.isQuilisma = true;
                    }
                }
                continue;
            }

            if (chantChar === "v") {
                const lastElement = neumeElements[neumeElements.length - 1];
                if (lastElement) {
                    const lastNote =
                        lastElement.notes[lastElement.notes.length - 1];
                    if (lastNote) {
                        lastNote.isVirga = true;
                    }
                }
                continue;
            }

            if (chantChar === "~") {
                const lastElement = neumeElements[neumeElements.length - 1];
                if (lastElement) {
                    const lastNote =
                        lastElement.notes[lastElement.notes.length - 1];
                    if (lastNote) {
                        lastNote.isLiquescent = true;
                    }
                }
                continue;
            }

            if (chantChar === " ") {
                const lastElement = neumeElements[neumeElements.length - 1];
                if (lastElement) {
                    const lastNote =
                        lastElement.notes[lastElement.notes.length - 1];
                    if (lastNote) {
                        lastNote.addSpace = true;
                    }
                }
                continue;
            }

            if (chantChar === "_") {
                continue; // Turned off
                const lastElement = neumeElements[neumeElements.length - 1];

                if (lastElement) {
                    for (let j = lastElement.notes.length - 1; j >= 0; j--) {
                        const prevNote = lastElement.notes[j];

                        if (!prevNote.hasHorizontalEpisema) {
                            prevNote.hasHorizontalEpisema = true;
                            break;
                        }
                    }
                }

                continue;
            }

            if (chantChar === ",") {
                neumeElements.push({ type: "quarter bar", notes: [] });
                continue;
            }

            if (chantChar === ";") {
                neumeElements.push({ type: "half bar", notes: [] });
                continue;
            }

            if (chantChar === ":") {
                neumeElements.push({ type: "full bar", notes: [] });
                continue;
            }

            const pitch = getPitchFromChar(chantChar);

            if (rawElement.chant[i + 1] === "x") {
                i += 1;
                flatPitch = pitch;
                showFlat = true;
                continue;
            }

            if (rawElement.chant[i + 1] === "y") {
                i += 1;
                flatPitch = undefined;
                // TODO: show natural sign
                continue;
            }

            if (isInclinatum(chantChar)) {
                const lastElement = neumeElements[neumeElements.length - 1];

                if (lastElement) {
                    if (lastElement.type === "podatus") {
                        lastElement.type = "pes subpunctis";
                        lastElement.notes.push({ pitch, isInclinatum: true });
                    } else if (
                        lastElement.type === "pes subpunctis" &&
                        pitch <
                            lastElement.notes[lastElement.notes.length - 1]
                                .pitch
                    ) {
                        lastElement.notes.push({ pitch, isInclinatum: true });
                    } else if (
                        lastElement.type === "punctum" &&
                        pitch <
                            lastElement.notes[lastElement.notes.length - 1]
                                .pitch
                    ) {
                        lastElement.type = "climacus";
                        lastElement.notes.push({ pitch, isInclinatum: true });
                    } else if (
                        lastElement.type === "climacus" &&
                        pitch <
                            lastElement.notes[lastElement.notes.length - 1]
                                .pitch
                    ) {
                        lastElement.notes.push({ pitch, isInclinatum: true });
                    } else {
                        neumeElements.push({
                            type: "punctum",
                            notes: [{ pitch, isInclinatum: true }],
                        });
                    }
                }

                requirePunctum = true;
            } else if (requirePunctum) {
                neumeElements.push({ type: "punctum", notes: [{ pitch }] });
                requirePunctum = false;
            } else {
                const lastElement = neumeElements[neumeElements.length - 1];

                if (
                    lastElement.type === "punctum" &&
                    pitch > lastElement.notes[0].pitch
                ) {
                    lastElement.type = "podatus";
                    lastElement.notes.push({ pitch });
                } else if (
                    lastElement.type === "punctum" &&
                    pitch < lastElement.notes[0].pitch &&
                    !lastElement.notes[0].isVirga
                ) {
                    lastElement.type = "clivis";
                    lastElement.notes.push({ pitch });
                } else if (
                    lastElement.type === "podatus" &&
                    pitch > lastElement.notes[1].pitch
                ) {
                    lastElement.type = "scandicus";
                    lastElement.notes.push({ pitch });
                } else if (
                    lastElement.type === "podatus" &&
                    pitch < lastElement.notes[1].pitch
                ) {
                    lastElement.type = "torculus";
                    lastElement.notes.push({ pitch });
                } else if (
                    lastElement.type === "clivis" &&
                    pitch > lastElement.notes[1].pitch &&
                    !lastElement.notes[0].hasHorizontalEpisema &&
                    !lastElement.notes[1].hasHorizontalEpisema
                ) {
                    lastElement.type = "porrectus";
                    lastElement.notes.push({ pitch });
                } else {
                    neumeElements.push({
                        type: "punctum",
                        notes: [{ pitch }],
                    });
                    requirePunctum = false;
                }
            }

            if (pitch === flatPitch) {
                const lastElement = neumeElements[neumeElements.length - 1];
                if (lastElement) {
                    const lastNote =
                        lastElement.notes[lastElement.notes.length - 1];
                    lastNote.isNoteFlat = true;
                }
            }
        }

        let trailingSpaces = "";
        while (
            rawElement.text[
                rawElement.text.length - trailingSpaces.length - 1
            ] === " "
        ) {
            trailingSpaces += " ";
        }
        const isBold =
            rawElement.text[0] === "*" &&
            rawElement.text[
                rawElement.text.length - trailingSpaces.length - 1
            ] === "*";

        let text = rawElement.text;

        const textToRender = isBold
            ? text.slice(
                  1,
                  rawElement.text.length - trailingSpaces.length - 1
              ) + trailingSpaces
            : text;

        neumes.push({
            elements: neumeElements.map((el) => {
                return {
                    ...el,
                    q: el.notes.map((n) => n.pitch).join(" "),
                };
            }),
            text: textToRender,
            isBold,
            flatPitch,
            showFlat,
            hasRedDotBefore: rawElement.hasRedDotBefore,
        });

        showFlat = false;

        if (trailingSpaces.length > 0) {
            flatPitch = undefined;
        }
    });

    return { neumes, dropCapText };
};
